import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import trackEvent from '@/client/utils/analytics/trackEvent';
import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import experiments from '@/utils/labs/experiments';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import pushToRouter from '@/client/utils/pushToRouter';
import removeSavedSearchFlag from '@/client/utils/searchRequestUtils';
import { Pill, Pills, TextRegular, SuccessBadge } from '@hz-design-system/web-ui';
import getIsMobile from '@/client/utils/browser/isMobile';
import CATEGORIES_L1 from '@/constants/categories';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import Classes from './CategorySuggestionTooltip.module.scss';
import HighlightGuide from '@/client/components/HighlightGuide/HighlightGuide';

type TCategorySuggestionTooltipProps = {
  suggestedCategories?: {
    id: number;
    name?: string;
    parentId?: number;
    parentName?: string;
    parentKey?: string;
    categoryKey?: string;
  }[];
  searchRequestObject: TSearchRequestObject;
  isSticky: boolean;
  showHighlight: boolean;
};

const CategorySuggestionTooltip: React.FC<TCategorySuggestionTooltipProps> = ({
  suggestedCategories,
  searchRequestObject,
  isSticky,
  showHighlight,
}) => {
  const { t } = useI18nContext();
  const { labsConfig } = useContext(EnvironmentContext);
  const { isOnlySuggestionTooltipEnabled } = experiments({
    labsConfig,
  });

  const filteredSuggestedCategories = useMemo(() => {
    if (!suggestedCategories?.length) return suggestedCategories;
    if (isOnlySuggestionTooltipEnabled) {
      return suggestedCategories.filter((category) => category.parentId !== CATEGORIES_L1.CARS);
    }
    return suggestedCategories;
  }, [suggestedCategories, isOnlySuggestionTooltipEnabled]);
  const [showGuide, setShowGuide] = useState(false);
  const [triggerGuideUpdate, setTriggerGuideUpdate] = useState(0);
  const targetRef = useRef<HTMLDivElement>(null);
  const LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY = 'LrpSuggestionHighlightGuideSeen';

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem(LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY) ?? '0';
    if (hasSeenGuide === '0' && filteredSuggestedCategories?.length && showHighlight) {
      setShowGuide(true);
    }
  }, [showHighlight, filteredSuggestedCategories?.length]);

  const handleCloseGuide = () => {
    localStorage.setItem(LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY, '1');
    setShowGuide(false);
  };
  useEffect(() => {
    if (!filteredSuggestedCategories?.length || !isSticky) return;

    const header = document.querySelector('.hz-Header');
    if (!header) return;

    const filterChipsElement = document.querySelector('.pillsContainer');

    let pendingAnimationFrameId: number | null = null;

    const observer = new ResizeObserver(() => {
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
      pendingAnimationFrameId = window.requestAnimationFrame(() => {
        const height = header.getBoundingClientRect().height;
        if (filterChipsElement) {
          const filterChipsHeight = filterChipsElement.getBoundingClientRect().height;
          const isMobile = getIsMobile();
          if (filteredSuggestedCategories?.length && filterChipsHeight > 0) {
            document.documentElement.style.setProperty('--filter-margin-bottom', isMobile ? '0px' : '8px');
          } else {
            document.documentElement.style.removeProperty('--filter-margin-bottom');
          }
          const extraHeight = filterChipsHeight > 0 ? 4 : 0;
          const mobileHeight = isMobile ? 4 : 0;
          document.documentElement.style.setProperty(
            '--category-suggested-height',
            `${height + filterChipsHeight + extraHeight + mobileHeight}px`,
          );
          if (showGuide) {
            setTriggerGuideUpdate((prev) => prev + 1);
          }
        }
        pendingAnimationFrameId = null;
      });
    });

    observer.observe(header);
    if (filterChipsElement) {
      observer.observe(filterChipsElement);
    }

    return () => {
      observer.disconnect();
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
    };
  }, [filteredSuggestedCategories?.length, isSticky, showGuide]);

  if (!filteredSuggestedCategories?.length) {
    return null;
  }
  const redirectURL = (category: {
    id: number;
    name?: string;
    parentId?: number;
    parentName?: string;
    parentKey?: string;
    categoryKey?: string;
  }) => {
    if (showGuide) {
      handleCloseGuide();
    }
    trackEvent({
      eventAction: 'CategoryTooltipClick',
      eventLabel: category.id?.toString() || '',
      eventMetric: { eventCategory: 'LRP' },
    });

    const categories = category.id
      ? {
          l1Category: {
            id: category.parentId || 0,
            key: category.parentKey,
          },
          l2Category: {
            id: category.id,
            key: category.categoryKey,
          },
        }
      : undefined;

    if (categories === undefined) {
      searchRequestObject.categories = {};
    }

    const options = {
      query: searchRequestObject.searchQuery?.trim() || '',
      categories: categories,
      searchRequestObject: removeSavedSearchFlag(searchRequestObject),
      withAllAttributes: false,
      removeAllFilters: false,
      removeAllAttributes: searchRequestObject?.categories?.l2Category?.id ? true : false,
    };
    const href = linkGeneratorHelper(options);
    const query = getClientPageQueryObject(generateLinkObject(options));

    pushToRouter({ query, href });
  };
  return (
    <>
      <div ref={targetRef} className={isSticky ? Classes.stickyBg : Classes.plainBg} suppressHydrationWarning={true}>
        <Pills>
          <TextRegular className={Classes.textRegular}>
            <SuccessBadge className={Classes.successBadge}>{t('header.navBar.userLinks.smbBundles.new')}</SuccessBadge>
            {t('header.searchBar.searchCategories')}:
          </TextRegular>
          {filteredSuggestedCategories?.map((category) => (
            <Pill
              className={Classes.childPill}
              translations={{
                ariaLabelDefault: (category.id === 0 ? t('header.searchBar.allCategories') : category.name) || '',
                ariaLabelActive: (category.id === 0 ? t('header.searchBar.allCategories') : category.name) || '',
              }}
              key={category.id}
              onClick={() => redirectURL(category)}
              data-testid={`category-suggestion-${category.id}`}
            >
              {category.id === 0 ? t('header.searchBar.allCategories') : category.name || ''}
            </Pill>
          ))}
        </Pills>
      </div>
      <div>
        <HighlightGuide
          targetRef={targetRef}
          show={showGuide}
          title={t('header.searchBar.suggestionGuideTitle')}
          description={t('header.searchBar.suggestionGuideContent')}
          onClose={handleCloseGuide}
          updateTrigger={triggerGuideUpdate}
          doneText={t('header.searchBar.done')}
        />
      </div>
    </>
  );
};

export default CategorySuggestionTooltip;
